
export type Handler<T> = (payload: T) => void

export class EventBus<EventMap extends Record<string, unknown>> {
  private handlers: Map<keyof EventMap, Set<Handler<unknown>>> = new Map()

  on<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): () => void {
    const set = (this.handlers.get(event) ?? new Set()) as Set<Handler<unknown>>
    set.add(handler as Handler<unknown>)
    this.handlers.set(event, set)
    return () => this.off(event, handler)
  }

  off<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): void {
    this.handlers.get(event)?.delete(handler as Handler<unknown>)
  }

  emit<K extends keyof EventMap>(event: K, payload: EventMap[K]): void {
    const set = this.handlers.get(event)
    if (!set) return
    set.forEach((h) => {
      try {
        ;(h as Handler<EventMap[K]>)(payload)
      } catch (err) {
        console.error('[eventBus] handler threw', err)
      }
    })
  }
}

import type { Trophy } from '@/milestones/types'
import type { LiveEvent } from '@/data/types'

export type AppEventMap = {
  'live-event': LiveEvent
  'trophy-unlocked': Trophy
  'trophy-tier-up': { category: string; newTier: number }
  'celebration:start': { id: string }
  'celebration:end': { id: string }
}

export const appBus = new EventBus<AppEventMap>()
